import { useEffect, useMemo, useState } from "react";
import { useCategoryList } from "../../../hooks/api/category/hooks";
import { ICategory } from "../../../types/api/category";

interface UseCategoryTableStateArgs {
  /**
   * "null"  -> top-level (parent) grid
   * string  -> child grid, scoped to this parent's _id
   * null    -> child grid with no parent selected yet (disabled)
   */
  parentId: string | "null" | null;
}

/**
 * One hook, reused for both the parent grid and the child grid.
 * Same API (useCategoryList), same shape of state — the only thing
 * that changes between the two callers is which parentId gets sent.
 */
export function useCategoryTableState({ parentId }: UseCategoryTableStateArgs) {
  const [search, setSearchRaw] = useState("");
  const [statusFilter, setStatusFilterRaw] = useState("");
  const [sortBy, setSortByRaw] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const enabled = parentId !== null;

  // Reset to page 1 whenever the scope changes (e.g. a different parent is selected)
  useEffect(() => {
    setPage(1);
  }, [parentId]);

  const queryData = useMemo(() => {
    if (!enabled) return null;
    const q: any = { page, limit, parentId };
    if (search.trim()) q.search = search.trim();
    if (statusFilter !== "") q.status = statusFilter;
    if (sortBy) q.sortBy = sortBy;
    return q;
  }, [page, limit, search, statusFilter, sortBy, parentId, enabled]);

  const { data, isLoading, isError, refetchWithQuery } = useCategoryList(
    queryData || { parentId: "none" },
    { enabled },
  );

  useEffect(() => {
    if (queryData) refetchWithQuery(queryData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData]);

  const list: ICategory[] = (data?.data as ICategory[]) ?? [];
  const total = Number(data?.extra?.total ?? list.length);

  const setSearch = (v: string) => {
    setSearchRaw(v);
    setPage(1);
  };

  const setStatusFilter = (v: string) => {
    setStatusFilterRaw(v);
    setPage(1);
  };

  const setSortBy = (v: string) => {
    setSortByRaw(v);
    setPage(1);
  };

  const setPageAndRefetch = (nextPage: number, nextLimit?: number) => {
    if (nextLimit !== undefined && nextLimit !== limit) {
      setLimit(nextLimit);
      setPage(1);
      if (queryData) refetchWithQuery({ ...queryData, page: 1, limit: nextLimit });
    } else {
      setPage(nextPage);
      if (queryData) refetchWithQuery({ ...queryData, page: nextPage });
    }
  };

  return {
    list,
    total,
    isLoading,
    isError,
    queryData,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    page,
    limit,
    setPage: setPageAndRefetch,
    refetch: refetchWithQuery,
  };
}
